const REDACTED = "[redacted]";

const SENSITIVE_QUERY_KEY =
  /token|invite|claim|code|password|secret|authorization|credential|phone|search|query|name|id|slug|next|redirect|return|signature/i;

function decodeUrlPart(value: string): string {
  let decoded = value;
  while (/%[\da-f]{2}/i.test(decoded)) {
    const next = decodeURIComponent(decoded);
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
}

export function redactAnalyticsUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, "https://www.pharmalogic.uz");
    // Token routes own the whole suffix: even a short token or an encoded slash is sensitive.
    const pathname = decodeUrlPart(url.pathname)
      .replace(/(\/app\/auto-selection|\/invitations\/accept)\/.*/i, `$1/${REDACTED}`)
      .replace(/(\/organization-invitations)\/[^/]+/i, `$1/${REDACTED}`)
      .split("/")
      .map((segment) => (/^\d+$|^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(segment) ? REDACTED : segment))
      .join("/");
    url.pathname = pathname;

    for (const key of Array.from(url.searchParams.keys())) {
      const normalizedKey = decodeUrlPart(key).replace(/[^a-z\d]/gi, "");
      if (SENSITIVE_QUERY_KEY.test(normalizedKey) || /^(q|key|sig|state|otp)$/i.test(normalizedKey)) {
        url.searchParams.set(key, REDACTED);
      } else {
        url.searchParams.set(key, redactAnalyticsText(url.searchParams.get(key) ?? ""));
      }
    }

    // Fragments can contain access tokens too; this app does not use hash routing.
    const origin = /^https?:\/\//i.test(rawUrl) ? url.origin : rawUrl.startsWith("//") ? `//${url.host}` : "";
    return origin + (rawUrl.startsWith("?") || rawUrl.startsWith("#") ? "" : url.pathname) + url.search;
  } catch {
    return REDACTED;
  }
}

export function redactAnalyticsText(value: string): string {
  let decoded: string;
  try {
    decoded = decodeUrlPart(value);
  } catch {
    return REDACTED;
  }

  if (/^(https?:\/\/|\/|\?|#)/i.test(decoded)) {
    return redactAnalyticsUrl(/^(https?:\/\/|\/|\?|#)/i.test(value) ? value : decoded);
  }

  const embeddedUrl = /https?:\/\/[^\s"'<>]+|\/(?:app|auth|invitations)(?:[/?#][^\s"'<>]*)?/gi;
  return embeddedUrl.test(decoded) ? decoded.replace(embeddedUrl, redactAnalyticsUrl) : value;
}

// PostHog copies URLs into attribution, exception frames, and nested person properties.
export function redactAnalyticsData<T>(data: T): T {
  if (typeof data === "string") return redactAnalyticsText(data) as T;
  if (Array.isArray(data)) return data.map(redactAnalyticsData) as T;
  if (data && typeof data === "object" && Object.getPrototypeOf(data) === Object.prototype) {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, redactAnalyticsData(value)])) as T;
  }
  return data;
}
